import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { useLayout } from "@/contexts/LayoutContext";
import { WhatsNewEntry } from "@/types/domain";
import { WhatsNewModal } from "./WhatsNewModal";
import { hasUnseenWhatsNew, writeWhatsNewSeen } from "./seen";

type WhatsNewContextValue = {
  entries: WhatsNewEntry[];
  hasUnseen: boolean;
  openWhatsNew: () => void;
}

const NO_ENTRIES: WhatsNewEntry[] = [];

const WhatsNewContext = createContext<WhatsNewContextValue>({ entries: NO_ENTRIES, hasUnseen: false, openWhatsNew: () => {} });

export const useWhatsNew = () => useContext(WhatsNewContext);

export const WhatsNewProvider = ({ children }: { children: ReactNode }) => {
  const { data } = useLayout();
  const entries = data.whatsNew ?? NO_ENTRIES;
  const latestId = entries[0]?.id;
  const [open, setOpen] = useState(false);
  const [hasUnseen, setHasUnseen] = useState(() => hasUnseenWhatsNew(latestId));

  const openWhatsNew = useCallback(() => {
    if (!latestId) return;
    writeWhatsNewSeen(latestId);
    setHasUnseen(false);
    setOpen(true);
  }, [latestId]);

  const value = useMemo(() => ({ entries, hasUnseen, openWhatsNew }), [entries, hasUnseen, openWhatsNew]);

  return (
    <WhatsNewContext.Provider value={value}>
      {children}
      {entries.length > 0 && <WhatsNewModal entries={entries} open={open} onOpenChange={setOpen} />}
    </WhatsNewContext.Provider>
  );
}
